
import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useDrop } from 'react-dnd';

// Generic imports
import CurrencyList from 'currency-list';
import { v4 as uuidv4 } from 'uuid';
import _ from 'lodash';
import axios from 'axios';
import dayjs from 'dayjs';
import { validitySentence } from '../../utils/date.js';
import pluralize from 'pluralize';

// External imports
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';

// MUI imports
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Paper from '@mui/material/Paper';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import Tooltip from '@mui/material/Tooltip';
import { grey, red } from '@mui/material/colors';
import { useTheme } from '@mui/material/styles';

// FontAwesome imports
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCirclePlus, faCopy, faSquare, faCheckSquare, faCheck, faGripDotsVertical } from '@fortawesome/pro-light-svg-icons';

// Local imports
import { checkProductErrors, findMatchingProducts } from '../utils/product.js';
import ContextMenu from '../components/ContextMenu.js';
import currencyToText from '../../CurrencyToText';
import ProfitWidget from '../components/ProfitWidget.js';
import { offerToText } from './OpportunityOneOffEmailBuilder.utils.js';
import getBaseUrl from '../../GetBaseUrl.js';
import { formulaToReact, addMarginToFormula } from '../../utils/Formula.js';
import { calcFieldChargeLineValue } from '../../utils/opportunity.js';
import { minimizeProducts, maximizeProducts, getDisplayRate } from '../utils/opportunity.js';
import { FieldTypes } from '../../FieldTypes.js';
import { ratesToText, getTotalRates, convertRateKgToLbsIfNeeded } from '../utils/rates.js';
import FieldSettingsDialog from './OpportunityOneOffEmailBuilder.fielddialog.js';
import SectionSettingsDialog from './OpportunityOneOffEmailBuilder.sectiondialog.js';
import OfferSettingsDialog from './OpportunityOneOffEmailBuilder.offerdialog.js';
import TermsDialog from './OpportunityOneOffBuilder.terms.js';

function OpportunityOneOffEmailBuilder(props) {
    const { offers, setOffers, buyingRates, setBuyingRates, products, setProducts, 
        org, accesstoken, transportationMethod, setTransportationMethod, 
        exchangeRates, account, routes, setRoutes, incoterm, preface, setPreface, 
        epilogue, setEpilogue, dirty, errors, terms, setTerms, opportunityId, lockedExchangeRates, suppliers
    } = props;

    const [ copied, setCopied ] = useState(null);
    const [ offerTemplate, setOfferTemplate ] = useState(null);
    const [ showBuyingRates, setShowBuyingRates ] = useState(false);
    const [ termsOpen, setTermsOpen ] = useState(false);
    const theme = useTheme();

    useEffect(() => {
        if (incoterm) {
            setOfferTemplate('show');
        }
    }, [ incoterm ])

    useEffect(() => {
        if (_.isNull(preface)) {
            let preface = 'Hi [Account Name],\n\nHere\'s our offer for the shipment:';
            if (account) {
                preface = preface.replace(/\[Account Name\]/g, _.get(account, 'contacts[0].firstName', '[Account Name]').trim());
            }
            setPreface(preface);
        } else if (account && preface.includes('[Account Name]')) {
            setPreface(preface.replace(/\[Account Name\]/g, _.get(account, 'contacts[0].firstName', '[Account Name]').trim()));
        }
        if (_.isNull(epilogue)) {
            const closing = _.get(org, 'defaults.defaultStrings.emailClosing', 'Best Regards,');
            setEpilogue(`Let us know if you have any questions.\n\n${closing},\nYour Team`);
        }
    }, [preface, epilogue, account])

    useEffect(() => {
        if (preface && account && account.contacts) {
            setPreface(preface.replace(/\[Account Name\]/g, _.get(account, 'contacts[0].firstName', '[Account Name]').trim()));
        }
    }, [account, preface])

    async function getLink() {
        const { data: postedOpportunity } = await axios.put(`/api/opportunities/${opportunityId}?organizationId=${org.id}&accesstoken=${accesstoken}`, {
            opportunity: {
                status: 'sent-to-endcustomer',
            }
        });
        const { data: url } = await axios(`/api/opportunities/${postedOpportunity.id}/offer-url?organizationId=${org.id}&accesstoken=${accesstoken}`);
        return `${getBaseUrl()}/offers?temporaryAccesstoken=${url}`;
    }

    async function onTopLineCopy() {
        setCopied('copying');
        const link = await getLink();

        // If terms is explicitly set (even if empty array), use it. Otherwise use default from org.
        const _terms = (terms !== null && terms !== undefined) ? terms : (_.get(org, `terms['price-quote-${transportationMethod}']`, null) || _.get(org, 'terms[\'price-quote\']', null));
        const __terms = !!_terms && _.isString(_terms) ? [{ value: _terms }] : _terms;

        let str = offerToText({ 
            offers, preface, epilogue, products, transportationMethod, buyingRates, exchangeRates, terms: __terms, link, lockedExchangeRates, organization: org });
        str = str.replace('PLACEHOLDER_FOR_LINK', link);
        function listener(e) {
            e.clipboardData.setData('text/html', str);
            e.clipboardData.setData('text/plain', str);
            e.preventDefault();
        }
        document.addEventListener('copy', listener);
        document.execCommand('copy');
        document.removeEventListener('copy', listener);
        setCopied('copied');
        await new Promise(resolve => setTimeout(resolve, 2000));
        setCopied(null);
    }

    async function onBreakdownCopy() {
        setCopied('copying');
        const link = await getLink();

        // If terms is explicitly set (even if empty array), use it. Otherwise use default from org.
        const _terms = (terms !== null && terms !== undefined) ? terms : (_.get(org, `terms['price-quote-${transportationMethod}']`, null) || _.get(org, 'terms[\'price-quote\']', null));
        const __terms = !!_terms && _.isString(_terms) ? [{ value: _terms }] : _terms;

        let str = offerToText({ 
            offers, preface, epilogue, products, transportationMethod, buyingRates, exchangeRates, expanded: true, terms: __terms, link, lockedExchangeRates, organization: org });
        str = str.replace('PLACEHOLDER_FOR_LINK', link);
        function listener(e) {
            e.clipboardData.setData('text/html', str);
            e.clipboardData.setData('text/plain', str);
            e.preventDefault();
        }
        document.addEventListener('copy', listener);
        document.execCommand('copy');
        document.removeEventListener('copy', listener);
        setCopied('copied');
        await new Promise(resolve => setTimeout(resolve, 2000));
        setCopied(null);
    }

    function onSetOfferTemplate(formId) {
        setOfferTemplate(null);
        const index = offers.length;
        const route = routes.length === 0 ? null : index < routes.length ? routes[index] : null;

        const convertMap = {};

        const newOffer = { 
            id: uuidv4(), 
            template: formId,
            title: route ? `${route.source?.text || 'Eveywhere'} to ${route.destination?.text || 'Eveywhere'}` : '',
            description: '',
            sections: org.forms[formId].sections.map(s => ({
                id: uuidv4(),
                title: s.title,
                description: s.description,
                offers: [{
                    id: uuidv4(),
                    fields: s.fields.map(f => {
                        const id = uuidv4();
                        convertMap[f.id] = id;
                        return {
                            id,
                            template: f.id,
                            title: f.title,
                            type: f.type === 'per-unit' ? 'per-unit-type' : f.type,
                            values: f.type === 'string' ? {
                                string: { value: f.default?.value || '' },
                            } : {
                                ..._.fromPairs([...products, {id: 'flat'}, {id: 'custom'}].map(p => ([p.id, { currency: f.currency || s.defaultCurrency, value: 0, ...(f.default || {}) }]))),
                            },
                            cargowiseChargeCode: f.cargowiseChargeCode,
                        }
                    }),
                }]
            }))
        };

        newOffer.sections.forEach(section => section.offers.forEach(offer => offer.fields.forEach(field => _.forEach(field.values, (value, key) => {
            if (value.formula) _.forEach(convertMap, (destId, srcId) => value.formula = value.formula.replace(srcId.replace(/-/g, '_'), destId.replace(/-/g, '_')));
        }))));

        setOffers([...offers, newOffer]);
    }

    const matchingForms = !offerTemplate ? null : _(org.forms)
        .keys()
        .filter(f => org.forms[f].type === 'opportunity' && 
            !org.forms[f].deleted &&
            org.forms[f].transportationMethods?.includes(transportationMethod) &&
            org.forms[f].incoterms?.includes(incoterm)
        )
        .value();

    useEffect(() => {
        if (matchingForms?.length === 1 && offers.length === 0) {
            onSetOfferTemplate(matchingForms[0]);
        }
    }, [offers, matchingForms]);

    const productErrors = useMemo(() => {
        return products.map(p => checkProductErrors({ product: p }));
    }, [products]);

    const isTransportationMethodSet = !!transportationMethod;

    return (
        <Box>
            <Box sx={{ background: theme.palette.primary.main, padding: '6px 24px', mb: 3, color: theme.palette.primary.contrast, boxShadow: '0px -1px 1px rgba(0,0,0,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant='body1' sx={{ color: theme.palette.primary.contrastText }}>
                    Response Email Builder
                </Typography>
            </Box>
            {!isTransportationMethodSet && (
                <Alert severity='warning' sx={{ margin: '0px 24px 24px 24px' }}>
                    <AlertTitle>Transportation Method Required</AlertTitle>
                    Please select a transportation method before editing this opportunity.
                </Alert>
            )}
            {!_.isEmpty(matchingForms) && (
                <Alert
                    severity='info'
                    sx={{ margin: '24px' }}
                    action={
                        <Button size='small' color='inherit' onClick={() => setOfferTemplate(null)}>Close</Button>
                    }
                >
                    Create an offer from the following template:
                    <Box sx={{ display: 'flex', flexDirection: 'row', gap: '6px' }}>
                        {matchingForms.map(f => (
                            <Button variant='text' color='inherit' key={f} onClick={() => onSetOfferTemplate(f)} disabled={!isTransportationMethodSet}>
                                {org.forms[f].title}
                            </Button>
                        ))}
                    </Box>
                </Alert>
            )}
            <Paper sx={{ margin: '0px 24px 24px 24px', border: `1px solid ${theme.palette.border1}` }} elevation={0}>
                <div className='nonotch'>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                        <TextField
                            value={preface}
                            onChange={e => setPreface(e.target.value)}
                            fullWidth
                            multiline
                            disabled={!isTransportationMethodSet}
                            sx={{ flex: 1 }}
                        />
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            {(dirty || !isTransportationMethodSet) && (
                                <Tooltip title={dirty ? 'Please save the opportunity first' : 'Please select a transportation method first'}>
                                    <span>
                                        <IconButton color='primary' disabled={true} onClick={() => { }}>
                                            <FontAwesomeIcon style={{ fontSize: '18px' }} icon={faCopy} />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                            )}
                            {!dirty && isTransportationMethodSet && !copied && (
                                <Box sx={{ margin: '6px' }}>
                                    <ContextMenu
                                        icon={faCopy}
                                        color='primary'
                                        options={[
                                            {
                                                key: 1,
                                                label: 'Copy top line only',
                                                onClick: onTopLineCopy,
                                                icon: <FontAwesomeIcon icon={faCopy} />
                                            },
                                            {
                                                key: 2,
                                                label: 'Copy with breakdown',
                                                onClick: onBreakdownCopy,
                                                icon: <FontAwesomeIcon icon={faCopy} />
                                            },
                                        ]}
                                    />
                                </Box>
                            )}
                            {copied === 'copied' && (
                                <Typography variant='caption' sx={{ background: theme.palette.primary.main, color: theme.palette.primary.contrast, padding: '6px 12px', borderRadius: '3px', margin: '6px' }}>
                                    <FontAwesomeIcon icon={faCheck} />
                                    &nbsp;
                                    Copied!
                                </Typography>
                            )}
                            {copied === 'copying' && (
                                <Box sx={{ padding: '6px 12px' }}>
                                    <CircularProgress size={16} />
                                </Box>
                            )}
                            <Box sx={{ margin: '6px' }}>
                                <ContextMenu
                                    color='primary'
                                    options={[
                                        {
                                            key: 1,
                                            label: 'Show inline buying rates',
                                            onClick: () => { setShowBuyingRates(!showBuyingRates) },
                                            icon: <FontAwesomeIcon icon={showBuyingRates ? faCheckSquare : faSquare} />
                                        }
                                    ]}
                                />
                            </Box>
                        </Box>
                    </Box>
                    {offers.map((offer, index) => (
                        <Offer
                            key={offer.id}
                            offer={offer}
                            offers={offers}
                            setOffers={setOffers} 
                             buyingRates={buyingRates} 
                             setBuyingRates={setBuyingRates} 
                             products={products} 
                             setProducts={setProducts}
                             exchangeRates={exchangeRates} 
                             transportationMethod={transportationMethod} 
                             setTransportationMethod={setTransportationMethod}
                             routes={routes}
                             setRoutes={setRoutes}
                             path={`[${index}]`}
                             errors={errors}
                             showBuyingRates={showBuyingRates}
                             lockedExchangeRates={lockedExchangeRates}
                             organization={org}
                             suppliers={suppliers}
                             disabled={!isTransportationMethodSet}
                        />
                    ))}
                    <AddOfferButton
                        offers={offers}
                        setOffers={setOffers}
                        setBuyingRates={setBuyingRates}
                        transportationMethod={transportationMethod}
                        setTransportationMethod={setTransportationMethod}
                        products={products}
                        setProducts={setProducts}
                        routes={routes}
                        setRoutes={setRoutes}
                        organization={org}
                        disabled={!isTransportationMethodSet}
                    />
                    <TextField
                        value={epilogue}
                        onChange={e => setEpilogue(e.target.value)}
                        fullWidth
                        multiline
                        disabled={!isTransportationMethodSet}
                    />
                </div>
            </Paper>
            <Box sx={{ background: 'transparent', padding: '6px 24px', mb: 3, borderBottom: '1px solid rgba(59, 130, 246, 0.3)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant='body1' sx={{ fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#334155' }}>
                    Terms & Conditions
                </Typography>
            </Box>
            <Paper sx={{ margin: '0px 24px 24px 24px', padding: '12px', border: `1px solid ${theme.palette.border1}` }} elevation={0}>
                {!_.isEmpty(terms) && (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Typography variant='body1' sx={{ padding: '0px 24px' }}>
                            The following {pluralize('term', terms.length)} and {pluralize('condition', terms.length)} will be added to the bottom of the email: {terms.map((t, i) => (
                                <span key={t.name}>
                                    <Tooltip title={t.value}>
                                        <span style={{ textDecoration: 'underline' }}>{t.name ? _.startCase(t.name) : 'Custom terms'}</span>
                                    </Tooltip>
                                    {i < terms.length - 2 ? ',' : i === terms.length - 1 ? '' : ', and '}
                                </span>
                            ))}
                        </Typography>
                        <Button
                            variant='contained'
                            color='primary'
                            onClick={() => setTermsOpen(true)}
                            disabled={!isTransportationMethodSet}
                        >
                            Edit
                        </Button>
                    </Box>
                )}
                {(_.isNull(terms) || _.isUndefined(terms)) && (
                    <Alert severity='info' sx={{ margin: '0px 8px' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                            <Typography variant='caption' sx={{ fontSize: '11px' }}>
                                Using default terms & conditions{transportationMethod ? ` for ${transportationMethod}` : ''}.
                            </Typography>
                            <Button
                                variant='contained'
                                color='primary'
                                size='small'
                                onClick={() => setTermsOpen(true)}
                                disabled={!isTransportationMethodSet}
                                sx={{ textTransform: 'none', fontSize: '11px' }}
                            >
                                Edit
                            </Button>
                        </Box>
                    </Alert>
                )}
                {_.isArray(terms) && _.isEmpty(terms) && (
                    <Alert severity='info' sx={{ margin: '0px 8px' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                            <Typography variant='caption' sx={{ fontSize: '11px' }}>
                                Not using any terms & conditions.
                            </Typography>
                            <Button
                                variant='contained'
                                color='primary'
                                size='small'
                                onClick={() => setTermsOpen(true)}
                                disabled={!isTransportationMethodSet}
                                sx={{ textTransform: 'none', fontSize: '11px' }}
                            >
                                Edit
                            </Button>
                        </Box>
                    </Alert>
                )}
            </Paper>
            {termsOpen && (
                <TermsDialog
                    open={true}
                    onClose={() => setTermsOpen(false)}
                    onSave={(terms) => setTerms(terms)}
                    terms={terms}
                    transportationMethod={transportationMethod}
                    organization={org}
                />
            )}
        </Box>
    )
}

export default OpportunityOneOffEmailBuilder;

function Offer(props) {
    const {
        offer, offers, setOffers, buyingRates, setBuyingRates, 
        products, setProducts, exchangeRates, transportationMethod, setTransportationMethod, 
        routes, setRoutes, path, errors, showBuyingRates, lockedExchangeRates, organization, suppliers, disabled } = props;

    const [ dropOver, setDropOver ] = useState(null);
    const [ openSettingsDialog, setOpenSettingsDialog ] = useState(false);
    const [ droppedRate, setDroppedRate ] = useState(null);
    const first = useRef();
    const setDropOverRef = useRef();
    const addProductCostRef = useRef();
    const productsRef = useRef();
    setDropOverRef.current = setDropOver;
    addProductCostRef.current = addProductCost;
    productsRef.current = products;

    function addProductCost(rate) {
        setDroppedRate(rate);
    }

    const transportationMethodRef = useRef();
    transportationMethodRef.current = transportationMethod;

    const [ , drop ] = useDrop(() => ({ 
        accept: ['RateCard'],
        hover: (item, monitor) => {
            if (disabled || first.current) return;
            first.current = true;
            setDropOverRef.current(monitor.canDrop(item, monitor) ? 'candrop' + (item.rate.type === 'tariff' ? '-tariff' : '') : 'cantdrop');
            function isOver() {
                if (monitor.isOver()) {
                    setTimeout(isOver, 100);
                } else {
                    first.current = false;
                    setDropOverRef.current(null);
                }
            }
            setTimeout(isOver, 100);
        },
        drop: (item, monitor) => {
            if (disabled) return;
            addProductCostRef.current(item.rate);
        },
        canDrop: (item, monitor) => {
            if (disabled) return false;
            if (transportationMethodRef.current && item.rate.transportationMethod !== transportationMethodRef.current) return false;
            if (!_.isEmpty(productsRef.current) && findMatchingProducts({ product: item.rate.product, products: productsRef.current }).length === 0) return false;
            return true;
        },
    }));

    function setOffer({ offer, currencyBulkUpdate }) {
        const _offers = _.cloneDeep(offers.map(o => o.id === offer.id ? offer : o));
        if (currencyBulkUpdate) {
            _offers.forEach(o => o.sections.forEach(s => {
                s.displayCurrency = offer.displayCurrency;
                s.displayCurrencySource = offer.displayCurrencySource;
                s.offers.forEach(o => o.fields.forEach(f => {
                    f.displayCurrency = offer.displayCurrency;
                    f.displayCurrencySource = offer.displayCurrencySource;
                }))
            }));
        }
        setOffers(_offers);
        setOpenSettingsDialog(false);
    }

    function addProductOfferFromRateAndMargin({ rate, margin }) {
        // Convert rate from KG to LBS if needed before using its values
        const convertedRate = convertRateKgToLbsIfNeeded({ rate, opportunityProducts: products });
        const form = convertedRate.offer;

        const convertMap = {};

        let matchingProducts = findMatchingProducts({ product: convertedRate.product, products });

        if (_.isEmpty(matchingProducts)) {
            const newProduct = minimizeProducts({
                products: maximizeProducts({
                    products: [{ ...convertedRate.product, id: uuidv4() }]
                }),
                transportationMethod: convertedRate.transportationMethod
            })[0];
            setProducts(products => [...products, newProduct ]);
            matchingProducts = [newProduct];
        }

        if (!transportationMethod) {
            setTransportationMethod(convertedRate.transportationMethod);
        }

        if (convertedRate.source && convertedRate.destination) {
            if (!routes || !routes.find(r => ((r.source || convertedRate.source).id === convertedRate.source.id && (r.destination || convertedRate.destination).id === convertedRate.destination.id))) {
                setRoutes([...routes || [], { source: convertedRate.source, destination: convertedRate.destination }]);
            }
        }

        const newOffer = _.cloneDeep(offer);

        if (form.validUntil && !newOffer.validUntil) {
            newOffer.validUntil = form.validUntil;
        }

        if (form.transitTime && !newOffer.transitTime) {
            newOffer.transitTime = transportationMethod === 'air' ? form.transitTime : form.transitTime * 24;
        }

        fillSectionsFromRate({ rate: convertedRate, form, sections: newOffer.sections, matchingProducts, convertMap, margin, organization, transportationMethod });

        newOffer.sections.forEach(section => section.offers.forEach(offer => offer.fields.forEach(field => _.forEach(field.values, (value, key) => {
            if (value.formula) _.forEach(convertMap, (destId, srcId) => value.formula = value.formula.replace(srcId.replace(/-/g, '_'), destId.replace(/-/g, '_')));
        }))));

        if (convertedRate.type !== 'tariff') {
            setBuyingRates(buyingRates => {
                const existing = buyingRates.find(br => br.id === convertedRate.id);
                if (existing) {
                    convertedRate.offer.sections = _.uniqBy([...convertedRate.offer.sections, ...existing.offer.sections], s => s.id);
                    return buyingRates.map(br => br.id === convertedRate.id ? convertedRate : br);
                } else {
                    return [...buyingRates, convertedRate];
                }
            });
        };

        setOffers(_offers => _offers.map(o => o.id === offer.id ? newOffer : o));
    }

    const offerConvertToCurrency = offer.displayCurrency?.convert ? offer.displayCurrency.to : (_.isString(offer.displayCurrency) ? offer.displayCurrency : null);

    return (
        <Box sx={{ padding: '0px 15px', paddingBottom: '15px', position: 'relative' }} ref={drop}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box
                    sx={{ cursor: disabled ? 'not-allowed' : 'pointer', textDecoration: 'underline', textDecorationStyle: 'dotted', opacity: disabled ? 0.5 : 1 }}
                    onClick={() => !disabled && setOpenSettingsDialog(true)}
                >
                    {offer.title || <i>No Title</i>}
                </Box>
                <Box sx={{ opacity: 0.7 }}>
                    <ProfitWidget 
                        text='Offer' 
                        section={{ displayCurrency: offer.displayCurrency, displayCurrencySource: offer.displayCurrencySource }}
                        sectionOffer={{ fields: offer.sections.map(s => s.offers.map(o =>  o.fields)).flat().flat()}} 
                        sections={offer.sections} 
                        products={products} 
                        buyingRates={buyingRates} 
                        transportationMethod={transportationMethod} 
                        exchangeRates={exchangeRates} 
                        lockedExchangeRates={lockedExchangeRates} 
                        organization={organization} 
                    />
                </Box>
            </Box>
            {errors && errors.map(e => e.id).includes(path.replace(/[\[\]]/g, '') + ':offertitle') && (
                <Alert severity='error' sx={{ margin: '3px 0px' }}>
                    Offers must have a titlte
                </Alert>
            )}
            {offer.description && (
                <Box sx={{ fontSize: '14px', color: grey[600], padding: '3px 0px' }}>
                    {offer.description}
                </Box>
            )}
            {offerConvertToCurrency && (
                <Box sx={{ fontSize: '12px', color: grey[500], fontStyle: 'italic' }}>
                    Offer total displayed as {CurrencyList.getAll('en_US')[offerConvertToCurrency].name}
                </Box>
            )}


            <DraggableList
                items={offer.sections}
                renderItem={(section, index) => (
                    <Box key={section.id}>
                        <Section
                            section={section}
                            offer={offer}
                            offers={offers}
                            setOffers={setOffers}
                            buyingRates={buyingRates}
                            setBuyingRates={setBuyingRates}
                            products={products}
                            path={`${path}.sections[${index}]`}
                            transportationMethod={transportationMethod}
                            exchangeRates={exchangeRates}
                            sections={offer.sections}
                            showBuyingRates={showBuyingRates}
                            lockedExchangeRates={lockedExchangeRates}
                            organization={organization}
                            suppliers={suppliers}
                            disabled={disabled}
                        />
                    </Box>
                )}
                onReorder={disabled ? undefined : ({ source, destination }) => {
                    setOffers(offers => {
                        const newOffers = _.cloneDeep(offers);
                        const _offer = newOffers.find(o => o.id === offer.id);
                        const [removed] = _offer.sections.splice(source, 1);
                        _offer.sections.splice(destination, 0, removed);
                        return newOffers;
                    });
                }}
            />

            <Button
                variant='text'
                color='primary'
                disabled={disabled}
                sx={{ borderRadius: '0px', justifyContent: 'flex-start', textTransform: 'none', padding: '6px 0px', minWidth: '0px' }}
                onClick={() => {
                    if (disabled) return;
                    setOffers(offers => {
                        const newOffers = _.cloneDeep(offers);
                        const _offer = _.get(offers, path);
                        _offer.sections.push({
                            id: uuidv4(),
                            title: 'New Section',
                            offers: [{
                                id: uuidv4(),
                                fields: []
                            }],
                        });
                        _.set(newOffers, path, _offer);
                        return newOffers;
                    });
                }}
            >
                <FontAwesomeIcon icon={faCirclePlus} />
                &nbsp;
                Add Section
            </Button>

            {offer.transitTime && (
                <Box sx={{ fontSize: '14px', padding: '3px 0px', cursor: disabled ? 'not-allowed' : 'pointer', textDecoration: 'underline', textDecorationStyle: 'dotted', opacity: disabled ? 0.5 : 1 }}
                    onClick={() => !disabled && setOpenSettingsDialog(true)}
                >
                    Transit Time: {(() => {
                        const v = parseFloat(offer.transitTime);
                        const mins = parseInt((v % 1) * 60);
                        const days = parseInt(parseInt(v) / 24);
                        const hours = parseInt(v) % 24;
                        return [
                            days ? (days === 1 ? `${days} day` : `${days} days`) : '',
                            hours ? (hours === 1 ? `${hours} hour` : `${hours} hours`) : '',
                            mins ? (mins === 1 ? `${mins} min` : `${mins} mins`) : '',
                        ].join(' ');
                    })()}
                </Box>
            )}

            {(offer.validUntil || offer.validFrom) && (
                <Box sx={{ fontSize: '14px', padding: '3px 0px', cursor: disabled ? 'not-allowed' : 'pointer', textDecoration: 'underline', textDecorationStyle: 'dotted', opacity: disabled ? 0.5 : 1 }}
                    onClick={() => !disabled && setOpenSettingsDialog(true)}
                >
                    {validitySentence(offer, d => dayjs(d).format('L'))}
                </Box>
            )}

            {dropOver === 'candrop' && (
                <Box sx={{ position: 'absolute', background: grey[800], opacity: 0.8, top: '0px', left: '0px', width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'flex-start', color: 'white', textAlign: 'start', paddingLeft: '24px' }}>
                    Drop your rate here
                </Box>
            )}

            {dropOver === 'cantdrop' && (
                <Box sx={{ position: 'absolute', background: red[800], opacity: 0.8, top: '0px', left: '0px', width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'flex-start', color: 'white', textAlign: 'start', paddingLeft: '24px', fontSize: '12px' }}>
                    Can't drop this rate here (transportation method do not match or no matching products found)
                </Box>
            )}

            {dropOver === 'candrop-tariff' && (
                <Box sx={{ position: 'absolute', background: grey[800], opacity: 0.8, top: '0px', left: '0px', width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'flex-start', color: 'white', textAlign: 'start', paddingLeft: '24px' }}>
                    Drop your tariff here
                </Box>
            )}

            <OfferSettingsDialog
                 open={openSettingsDialog} 
                 onClose={() => setOpenSettingsDialog(false)} 
                 offer={offer} 
                 setOffer={setOffer}
                 onDeleteOffer={() => {
                    setOffers(offers => offers.filter(o => o.id !== offer.id));
                    setOpenSettingsDialog(false);
                 }}
                 organization={organization}
                 exchangeRates={exchangeRates}
                 lockedExchangeRates={lockedExchangeRates}
            />

            <SetMarginDialog
                open={!!droppedRate}
                onClose={() => setDroppedRate(null)}
                onConfirm={({margin}) => addProductOfferFromRateAndMargin({ rate: droppedRate, margin: margin })}
            />
        </Box>
    )
}

function Section(props) {
    const { section, offer, offers, setOffers, setBuyingRates, products, 
        path, transportationMethod, exchangeRates, sections, buyingRates, 
        showBuyingRates, lockedExchangeRates, organization, suppliers, disabled } = props;

    const [ openSectionSettingsDialog, setOpenSectionSettingsDialog ] = useState(false);

    function setSection({ section, currencyBulkUpdate }) {
        setOffers(offers => {
            const newOffers = _.cloneDeep(offers);
            newOffers.forEach(o => o.sections = o.sections.map(s => s.id === section.id ? section : s));
            newOffers.forEach(o => o.sections.forEach(s => {
                if (s.id === section.id) {
                    s.displayCurrency = section.displayCurrency;
                    s.displayCurrencySource = section.displayCurrencySource;
                    if (currencyBulkUpdate) {
                        s.offers.forEach(o => o.fields.forEach(f => {
                            f.displayCurrency = section.displayCurrency;
                            f.displayCurrencySource = section.displayCurrencySource;
                        }))
                    }
                }
            }));
            return newOffers;
        });
        setOpenSectionSettingsDialog(false);
    }

    const sectionConvertToCurrency = section.displayCurrency?.convert ? section.displayCurrency.to : (_.isString(section.displayCurrency) ? section.displayCurrency : null);

    return (
        <Box sx={{ padding: '0px 15px' }}>
            <Box component='span' sx={{ cursor: disabled ? 'not-allowed' : 'pointer', textDecoration: 'underline', textDecorationStyle: 'dotted', opacity: disabled ? 0.5 : 1 }} onClick={() => !disabled && setOpenSectionSettingsDialog(true)}>
                {section.title === '***UNASSIGNED***' ? <i>Unassigned</i> : section.title || <i>No Title</i>}
            </Box>
            {sectionConvertToCurrency && (
                <Box sx={{ fontSize: '12px', color: grey[500], fontStyle: 'italic' }}>
                    Section total displayed as {CurrencyList.getAll('en_US')[sectionConvertToCurrency].name}
                </Box>
            )}
            {section.offers.map((sectionOffer, index) => (
                <SectionOffer
                    key={sectionOffer.id}
                    sectionOffer={sectionOffer}
                    offers={offers}
                    setOffers={setOffers}
                    buyingRates={buyingRates}
                    setBuyingRates={setBuyingRates}
                    products={products}
                    offer={offer}
                    section={section}
                    path={`${path}.offers[${index}]`}
                    transportationMethod={transportationMethod}
                    exchangeRates={exchangeRates}
                    sections={sections}
                    showBuyingRates={showBuyingRates}
                    lockedExchangeRates={lockedExchangeRates}
                    organization={organization}
                    suppliers={suppliers}
                    disabled={disabled}
                />
            ))}
            <SectionSettingsDialog
                open={openSectionSettingsDialog}
                onClose={() => setOpenSectionSettingsDialog(false)}
                section={section}
                setSection={setSection}
                onDeleteSection={() => {
                    setOffers(offers => {
                        const newOffers = _.cloneDeep(offers);
                        newOffers.forEach(o => o.sections = o.sections.filter(s => s.id !== section.id));
                        return newOffers;
                    });
                    setOpenSectionSettingsDialog(false);
                }}
                organization={organization}
                exchangeRates={exchangeRates}
                lockedExchangeRates={lockedExchangeRates}
            />
        </Box>
    )
}

function SectionOffer(props) {
    const { sectionOffer, setOffers, setBuyingRates, products, offer, section, path, 
        transportationMethod, exchangeRates, sections, buyingRates, showBuyingRates, 
        lockedExchangeRates, organization, suppliers, disabled } = props;

    return (
        <Box>
            <DraggableList
                items={sectionOffer.fields}
            renderItem={(field, index) => (
                <Box key={field.id}>
                    <Field
                        key={field.id}
                        field={field}
                        organization={organization}
                        setOffers={setOffers}
                        setBuyingRates={setBuyingRates}
                        buyingRates={buyingRates}
                        products={products}
                        offer={offer}
                        section={section}
                        transportationMethod={transportationMethod}
                        exchangeRates={exchangeRates}
                        sections={sections}
                        showBuyingRates={showBuyingRates}
                        lockedExchangeRates={lockedExchangeRates}
                        suppliers={suppliers}
                        disabled={disabled}
                    />
                </Box>
            )}
            onReorder={disabled ? undefined : ({ source, destination }) => {
                setOffers(offers => {
                    const newOffers = _.cloneDeep(offers);
                    const _sectionOffer = _.get(offers, path);
                    const [removed] = _sectionOffer.fields.splice(source, 1);
                    _sectionOffer.fields.splice(destination, 0, removed);
                    _.set(newOffers, path, _sectionOffer);
                    return newOffers;
                });
                }}
            />
            <Button
                 variant='text' 
                 color='primary' 
                 disabled={disabled}
                 sx={{ borderRadius: '0px', justifyContent: 'flex-start', textTransform: 'none', padding: '6px 0px', minWidth: '0px' }}
                 onClick={() => {
                    if (disabled) return;
                    setOffers(offers => {
                        const newOffers = _.cloneDeep(offers);
                        const _sectionOffer = _.get(offers, path);
                        _sectionOffer.fields.push({
                            id: uuidv4(),
                            title: 'New Field',
                            type: 'flat',
                            values: {
                                flat: {
                                    currency: _.values(offers[0]?.sections[0]?.offers[0]?.fields[0]?.values)[0]?.currency || organization.defaults?.currency,
                                    value: 0,
                                }
                            },
                            buyingRates: [],
                        });
                        _.set(newOffers, path, _sectionOffer);
                        return newOffers;
                    });
                }}
            >
                <FontAwesomeIcon icon={faCirclePlus} />
                &nbsp;
                Add Field
            </Button>
        </Box>
    );
}

function Field(props) {
    const { field, setOffers, setBuyingRates, products, transportationMethod, offer, sections, section,
        exchangeRates, buyingRates, showBuyingRates, lockedExchangeRates, organization, suppliers, disabled } = props;
    const [ openFieldSettingsDialog, setOpenFieldSettingsDialog ] = useState(false);

    function setField({ field }) {
        setOffers(offers => {
            const newOffers = _.cloneDeep(offers);
            newOffers.forEach(o => o.sections.forEach(s => s.offers.forEach(offer => {
                offer.fields = offer.fields.map(f => f.id === field.id ? field : f);
            })));
            newOffers.forEach(o => o.sections.forEach(s => s.offers.forEach(offer => offer.fields.forEach(f => {
                if (f.id === field.id) {
                    f.displayCurrency = field.displayCurrency;
                    f.displayCurrencySource = field.displayCurrencySource;
                }
            }))));
            return newOffers;
        });
    }

    const fieldConvertToCurrency = field.displayCurrency?.convert ? field.displayCurrency.to : (_.isString(field.displayCurrency) ? field.displayCurrency : null);

    return (
        <Box sx={{ paddingLeft: '15px' }}>
            <Box component='span' sx={{ cursor: disabled ? 'not-allowed' : 'pointer', textDecoration: 'underline', textDecorationStyle: 'dotted', opacity: disabled ? 0.5 : 1 }} onClick={() => !disabled && setOpenFieldSettingsDialog(true)}>
                {field.title || <i>No Title</i>}
            </Box>
            {fieldConvertToCurrency && (
                <Box sx={{ fontSize: '12px', color: grey[500], fontStyle: 'italic' }}>
                    Displaying as {CurrencyList.getAll('en_US')[fieldConvertToCurrency].name}
                </Box>
            )}
            {field.type === 'string' && <FieldString field={field} setOffers={setOffers} />}
            {field.type !== 'string' && (
                <FieldNonString 
                    field={field} 
                    setOffers={setOffers} 
                    setBuyingRates={setBuyingRates} 
                    products={products} 
                    sections={sections} 
                    transportationMethod={transportationMethod} 
                    exchangeRates={exchangeRates} 
                    buyingRates={buyingRates}
                    showBuyingRates={showBuyingRates}
                    lockedExchangeRates={lockedExchangeRates}
                    organization={organization}
                />
            )}

            {openFieldSettingsDialog && (
                <FieldSettingsDialog
                    open={openFieldSettingsDialog}
                    offers={[offer]}
                    onClose={() => setOpenFieldSettingsDialog(false)}
                    field={field}
                    buyingRates={buyingRates}
                    onDone={({ field, currencyBulkUpdate, buyingRates }) => {
                        setField({ field });
                        setBuyingRates(buyingRates);
                        setOpenFieldSettingsDialog(false);
                    }}
                    products={products}
                    sections={sections}
                    transportationMethod={transportationMethod}
                    lockedExchangeRates={lockedExchangeRates}
                    organization={organization}
                    suppliers={suppliers}
                    exchangeRates={exchangeRates}
                    onDeleteField={() => {
                        setOffers(offers => {
                            const newOffers = _.cloneDeep(offers);
                            newOffers.forEach(o => o.sections.forEach(s => s.offers.forEach(offer => {
                                offer.fields = offer.fields.filter(f => f.id !== field.id);
                            })));
                            return newOffers;
                        });
                    }}
                />
            )}
        </Box>
    )
}

function FieldString(props) {
    const { field } = props;

    const value = field.values.string;

    return (
        <Box sx={{ paddingLeft: '15px' }}>
            {value?.value || ''}
        </Box>
    )
}

function FieldNonString(props) {
    const { field, products, transportationMethod, sections, exchangeRates, buyingRates, showBuyingRates, lockedExchangeRates, organization } = props;
    const theme = useTheme();

    const fieldType = FieldTypes[transportationMethod].find(f => f.value === field.type);
    const chargeLines = fieldType?.getChargeLines ? fieldType.getChargeLines({ field, products, buyingRates, fieldBuyingRates: field.buyingRates, organization }) : [];

    return (
        <Box sx={{ paddingLeft: '15px' }}>
            {chargeLines.map(line => {
                const { title, quantityMultiplier: multiplier, multiplierText, multiplierTextSingular, fieldValueId, productsInChargeLine } = line;
                const productId = fieldValueId;
                const value = field.values[productId];

                const buyingRateText = ratesToText({ rates: line.buyingRates, transportationMethod, buyingRates, exchangeRates, products: productsInChargeLine, organization });
                const buyingRateNode = !_.isEmpty(buyingRateText) && buyingRateText.length > 1 && showBuyingRates ? (
                    <Box sx={{ paddingTop: '6px' }}>
                        <Alert severity='info' sx={{ fontSize: '11px' }}>
                            <AlertTitle sx={{ fontSize: '11px' }}>Cost Rates</AlertTitle>
                            <span dangerouslySetInnerHTML={{ __html: buyingRateText.join('') }} />
                        </Alert>
                    </Box>
                ) : null;

                if (value?.formula) {
                    const totalBuyingPrice = getTotalRates({ field, fieldBuyingRates: field.buyingRates, products: productsInChargeLine, transportationMethod, buyingRates, exchangeRates, skipSumCurrencies: true, organization });
                    const cost = _.sum(_.map(totalBuyingPrice, (val, currency) => val / exchangeRates[currency])) || 0;
                    const calcValue = calcFieldChargeLineValue({
                        field,
                        line,
                        cost,
                        sections,
                        products: productsInChargeLine,
                        transportationMethod,
                        exchangeRates,
                        organization,
                        buyingRates
                    });
                    const formulaValue = calcValue ? calcValue.sum[value.currency] || 0 : 0;
                    return ([
                        <Box key={productId}>
                            <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                                {title ? `${title}: ` : ''}
                                <Box sx={{ paddingLeft: '3px', display: 'flex', flexDirection: 'column' }}>
                                    {formulaToReact({ formula: value.formula, currency: value.currency, errorValue: value.formula, multiplierText: multiplierTextSingular, sections, width: 'auto', sx: {} })}
                                    <Box sx={{ paddingTop: '6px', marginTop: '6px', borderTop: `1px solid ${theme.palette.border1}` }}>
                                        {multiplierText !== 'shipment' ? `calculated for ${multiplier} ${multiplierText}:` : '='} {currencyToText(value.currency, true, formulaValue.toLocaleString({ maximumFractionDigits: 2 }))}
                                    </Box>
                                </Box>
                            </Box>
                            {buyingRateNode}
                        </Box>
                    ])
                }

                if (!value) return (
                    <Box key={productId} sx={{ opacity: 0.5, fontStyle: 'italic' }}>
                        {title ? `${title}: ` : ''}
                        [no rate]
                        {buyingRateNode}
                    </Box>
                );

                const displayRate = (() => {
                    const fieldConvertToCurrency = field.displayCurrency?.convert ? field.displayCurrency.to : (_.isString(field.displayCurrency) ? field.displayCurrency : null);
                    if (!fieldConvertToCurrency) return null;
                    const displayCurrencySource = field.displayCurrency?.source || field.displayCurrencySource;
                    const rate = getDisplayRate({
                        sourceCurrency: value.currency,
                        displayCurrency: fieldConvertToCurrency,
                        displayCurrencySource,
                        lockedExchangeRates,
                        liveRates: exchangeRates,
                        fixedRates: organization?.exchangeRates,
                        organization
                    });
                    return <span style={{ fontSize: '12px', color: grey[500] }}> x {rate.toLocaleString({ maximumFractionDigits: 3 })} {value.currency}/{fieldConvertToCurrency}</span>
                })();

                const rate = getDisplayRate({
                    sourceCurrency: value.currency,
                    displayCurrency: field.displayCurrency?.convert ? field.displayCurrency.to : organization.defaults?.currency,
                    displayCurrencySource: field.displayCurrencySource,
                    lockedExchangeRates,
                    liveRates: exchangeRates,
                    organization
                });
                return (
                    <Box key={productId}>
                        {title ? `${title}: ` : ''}
                        {currencyToText(value.currency, true, (value.value || 0).toLocaleString({ maximumFractionDigits: 2 }))} x {(multiplier || 0).toLocaleString({ maximumFractionDigits: 2 })} {multiplierText || ''}
                        {displayRate}
                        {buyingRateNode}
                    </Box>
                );
            })}
        </Box>
    )
}

function AddOfferButton(props) {
    const { offers, setOffers, setBuyingRates, products, setProducts, transportationMethod, setTransportationMethod, routes, setRoutes, organization, disabled } = props;
    const [ dropOver, setDropOver ] = useState(null);
    const [ droppedRate, setDroppedRate ] = useState(null);

    const first = useRef();
    const setDropOverRef = useRef();
    const createOfferFromRate = useRef();
    const transportationMethodRef = useRef();
    const productsRef = useRef();

    setDropOverRef.current = setDropOver;
    transportationMethodRef.current = transportationMethod;
    productsRef.current = products;

    const [ , drop ] = useDrop(() => ({ 
        accept: ['RateCard'],
        hover: (item, monitor) => {
            if (disabled || first.current) return;
            first.current = true;
            setDropOverRef.current(monitor.canDrop(item, monitor) ? 'candrop' + (item.rate.type === 'tariff' ? '-tariff' : '') : 'cantdrop');
            function isOver() {
                if (monitor.isOver()) {
                    setTimeout(isOver, 100);
                } else {
                    first.current = false;
                    setDropOverRef.current(null);
                }
            }
            setTimeout(isOver, 100);
        },
        drop: (item, monitor) => {
            if (disabled) return;
            createOfferFromRate.current(item.rate);
        },
        canDrop: (item, monitor) => {
            if (disabled) return false;
            if (transportationMethodRef.current && item.rate.transportationMethod !== transportationMethodRef.current) return false;
            if (!_.isEmpty(productsRef.current) && findMatchingProducts({ product: item.rate.product, products: productsRef.current }).length === 0) return false;
            return true;
        },
    }));

    createOfferFromRate.current = (rate) => {
        setDroppedRate(rate);
    }

    function createOfferFromRateAndMargin({ rate, margin }) {
        // Convert rate from KG to LBS if needed before using its values
        const convertedRate = convertRateKgToLbsIfNeeded({ rate, opportunityProducts: products });
        const form = convertedRate.offer;
        const route = { source: convertedRate.source, destination: convertedRate.destination };

        const convertMap = {};

        let matchingProducts = findMatchingProducts({ product: convertedRate.product, products });

        if (_.isEmpty(matchingProducts)) {
            const newProduct = minimizeProducts({
                products: maximizeProducts({
                    products: [{ ...convertedRate.product, id: uuidv4() }]
                }),
                transportationMethod: convertedRate.transportationMethod
            })[0];
            setProducts(products => [...products, newProduct ]);
            matchingProducts = [newProduct];
        }

        if (!transportationMethod) {
            setTransportationMethod(convertedRate.transportationMethod);
        }

        if (convertedRate.source && convertedRate.destination) {
            if (!routes || !routes.find(r => ((r.source || convertedRate.source).id === convertedRate.source.id && (r.destination || convertedRate.destination).id === convertedRate.destination.id))) {
                setRoutes([...routes || [], { source: convertedRate.source, destination: convertedRate.destination }]);
            }
        }

        const newOffer = { 
            id: uuidv4(), 
            title: (route ? `${route.source?.text || 'Eveywhere'} to ${route.destination?.text || 'Eveywhere'} using ` : '') + convertedRate.supplier.organization,
            description: '',
            sections: [],
            ...(convertedRate.type === 'spot' ? {} : { validUntil: convertedRate.offer.validUntil }),
        };

        if (convertedRate.offer.transitTime) {
            newOffer.transitTime = transportationMethod === 'air' ? convertedRate.offer.transitTime : convertedRate.offer.transitTime * 24;
        }

        fillSectionsFromRate({ rate: convertedRate, form, sections: newOffer.sections, matchingProducts, convertMap, margin, organization, transportationMethod });

        newOffer.sections.forEach(section => section.offers.forEach(offer => offer.fields.forEach(field => _.forEach(field.values, (value, key) => {
            if (value.formula) _.forEach(convertMap, (destId, srcId) => value.formula = value.formula.replace(srcId.replace(/-/g, '_'), destId.replace(/-/g, '_')));
        }))));

        if (convertedRate.type !== 'tariff') {
            setBuyingRates(buyingRates => {
                const existing = buyingRates?.find(br => br.id === convertedRate.id);
                if (existing) {
                    convertedRate.offer.sections = _.uniqBy([...convertedRate.offer.sections, ...existing.offer.sections], s => s.id);
                    return buyingRates?.map(br => br.id === convertedRate.id ? convertedRate : br);
                } else {
                    return [...(buyingRates || []), convertedRate];
                }
            });
        }

        setOffers([...offers, newOffer]);
    }
    return (
        <Box ref={drop} sx={{ position: 'relative' }}>
            <Button 
                fullWidth 
                variant='outlined' 
                color='primary' 
                disabled={disabled}
                sx={{ borderRadius: '0px', justifyContent: 'flex-start', textTransform: 'none' }}
                onClick={() => {
                    if (disabled) return;
                    setOffers(offers => [...offers, {
                        id: uuidv4(),
                        title: 'New Offer',
                        description: '',
                        sections: [{
                            id: uuidv4(),
                            title: 'New Section',
                            offers: [{
                                id: uuidv4(),
                                fields: []
                            }]
                        }]
                    }]);
                }}
            >
                <FontAwesomeIcon icon={faCirclePlus} />
                &nbsp;
                Add Offer (or drag rate here)
            </Button>

            {dropOver === 'candrop' && (
                <Box sx={{ position: 'absolute', background: grey[800], opacity: 0.8, top: '0px', left: '0px', width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'flex-start', color: 'white', textAlign: 'start', paddingLeft: '24px' }}>
                    Drop your rate here
                </Box>
            )}

            {dropOver === 'cantdrop' && (
                <Box sx={{ position: 'absolute', background: red[800], opacity: 0.8, top: '0px', left: '0px', width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'flex-start', color: 'white', textAlign: 'start', paddingLeft: '24px', fontSize: '12px' }}>
                    Can't drop this rate here (transportation method do not match or no matching products found)
                </Box>
            )}

            {dropOver === 'candrop-tariff' && (
                <Box sx={{ position: 'absolute', background: grey[800], opacity: 0.8, top: '0px', left: '0px', width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'flex-start', color: 'white', textAlign: 'start', paddingLeft: '24px' }}>
                    Drop your tariff here
                </Box>
            )}

            <SetMarginDialog
                open={!!droppedRate}
                onClose={() => setDroppedRate(null)}
                onConfirm={({margin}) => createOfferFromRateAndMargin({ rate: droppedRate, margin })}
            />
        </Box>
    )
}

function DraggableList(props) {
    const { items, renderItem, onReorder } = props;

    function onDragEnd(result) {
        if (!result.destination || !onReorder) return;
        onReorder({ source: result.source.index, destination: result.destination.index });
    }
    return (
        <DragDropContext onDragEnd={onDragEnd}>
            <Droppable droppableId='droppable'>
                {(provided) => (
                    <div {...provided.droppableProps} ref={provided.innerRef}>
                        {items.map((item, index) => (
                            <Draggable key={item.id} draggableId={item.id} index={index}>
                                {(provided) => (
                                    <div {...provided.draggableProps} ref={provided.innerRef}>
                                        <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                                            <div {...provided.dragHandleProps} style={{ marginTop: '4px' }}>
                                                <FontAwesomeIcon icon={faGripDotsVertical} />
                                            </div>
                                            {renderItem(item, index)}
                                        </Box>
                                    </div>
                                )}
                            </Draggable>
                        ))}
                        {provided.placeholder}
                    </div>
                )}
            </Droppable>
        </DragDropContext>
    )
}

function SetMarginDialog(props) {
    const { open, onClose, onConfirm } = props;
    const [ margin, setMargin ] = useState(0);
    return (
        <Dialog open={open} onClose={onClose} sx={{ zIndex: 1000000 }}>
            <DialogTitle>Set Margin</DialogTitle>
            <DialogContent>
                <DialogContentText>
                    Please enter the margin percentage to add to all rates before adding them.
                </DialogContentText>
                <TextField
                    autoFocus
                    margin='dense'
                    id='margin'
                    label='Margin (%)'
                    fullWidth
                    variant='standard'
                    value={margin}
                    onChange={(e) => setMargin(e.target.value)}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button onClick={() => {
                    onConfirm({ margin: (parseFloat(margin) || 0) / 100 });
                    onClose();
                }}>Add</Button>
            </DialogActions>
        </Dialog>
    );
}

function fillSectionsFromRate({ rate, form, sections, matchingProducts, convertMap, margin, organization, transportationMethod }) {
    form.sections.forEach(section => {
        let newOfferSection = sections.find(s => s.title === section.title);
        if (!newOfferSection) {
            newOfferSection = {
                id: uuidv4(),
                title: section.title,
                description: section.description,
                offers: [{
                    id: uuidv4(),
                    fields: []
                }]
            };
            sections.push(newOfferSection);
        }
        section.offers[0].fields.forEach(field => {
            let newField = newOfferSection.offers[0].fields.find(f => f.title === field.title);
            if (!newField) {
                newField = createFieldFromRate({ field, rate, matchingProducts, convertMap, defaultCurrency: section.defaultCurrency, margin, organization, transportationMethod });
                newOfferSection.offers[0].fields.push(newField);
            }

            if (field.type === 'string') {
                newField.values = {
                    ...newField.values,
                    string: {
                        value: field.values.string.value,
                    }
                }
            } else {
                newField.values = {
                    ...newField.values,
                    ..._.fromPairs(matchingProducts.map(p => ([p.id, {
                        currency: _.first(_.values(field.values))?.currency || section.defaultCurrency,
                        value: field.values[field.type === 'flat' ? 'flat' : rate.product.id].value * (1 + margin),
                        formula: addMarginToFormula({ formula: field.values[field.type === 'flat' ? 'flat' : rate.product.id].formula, margin }),
                    }]))),
                }
            }

            newField.buyingRates = _.uniqWith([
                ...newField.buyingRates,
                ...(rate.type === 'tariff' ? [] :
                    matchingProducts.map(p => ({
                        rateId: rate.id,
                        fieldId: field.id,
                        opportunityProductId: p.id,
                    })))
            ], _.isEqual);
        });
    });
}

function createFieldFromRate({ field, rate, matchingProducts, convertMap, defaultCurrency, margin, organization, transportationMethod }) {
    const id = uuidv4();
    convertMap[field.id] = id;
    
    // Check if field type is enabled, if not change to 'per-unit'
    let fieldType = field.type === 'per-unit' ? 'per-unit-type' : field.type;
    const fieldTypeConfig = FieldTypes[transportationMethod]?.find(ft => ft.value === fieldType);
    if (fieldTypeConfig?.enabled && !fieldTypeConfig.enabled({ organization })) {
        fieldType = 'per-unit';
    }
    
    return {
        id,
        template: field.id,
        title: field.title,
        type: fieldType,
        values: field.type === 'string' ? {
            string: {
                value: field.values.string.value,
            }
        } : {
            ..._.fromPairs([...matchingProducts, { id: 'flat' }, { id: 'custom' }].map(p => ([p.id, {
                currency: _.first(_.values(field.values))?.currency || defaultCurrency,
                value: field.values[field.type === 'flat' ? 'flat' : rate.product.id].value * (1 + margin),
                formula: addMarginToFormula({ formula: field.values[field.type === 'flat' ? 'flat' : rate.product.id].formula, margin }),
            }]))),
        },
        buyingRates: rate.type === 'tariff' ? [] :
            matchingProducts.map(p => ({
                rateId: rate.id,
                fieldId: field.id,
                opportunityProductId: p.id,
            })),
        cargowiseChargeCode: field.cargowiseChargeCode,
    }
}